/* eslint-disable */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSetting, setSetting, MODELS } from "../utils/settings";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";

const K = {
  photoAIFilter: 'wanderologie_settings.photoAIFilter',
  photoAIModel: 'wanderologie_settings.photoAIModel',
  aiModel: 'wanderologie_settings.aiModel',
  extendedCaching: 'wanderologie_settings.extendedCaching',
  currency: 'wanderologie_settings.currency',
  language: 'wanderologie_settings.language',
  showAvantLeDepart: 'wanderologie_settings.showAvantLeDepart',
  avantScope: 'wanderologie_settings.avantScope',
  showCostEstimates: 'wanderologie_settings.showCostEstimates',
  showBookingInfo: 'wanderologie_settings.showBookingInfo',
  showLocation: 'wanderologie_settings.showLocation',
};

const PHOTO_MODEL_OPTIONS = [
  { label: "Claude Haiku 4.5 (Fast)", value: MODELS.fast },
];

const AI_MODEL_OPTIONS = [
  { label: "Claude Haiku 4.5 (Fast)", value: "fast" },
  { label: "Claude Sonnet 4.6 (Balanced)", value: "balanced" },
  { label: "Claude Opus 4.6 (Premium)", value: "premium" },
];

const CURRENCY_OPTIONS = [
  { label: "USD ($)", value: "USD" },
  { label: "EUR (€)", value: "EUR" },
  { label: "GBP (£)", value: "GBP" },
  { label: "JPY (¥)", value: "JPY" },
  { label: "AUD (A$)", value: "AUD" },
  { label: "CAD (C$)", value: "CAD" },
];

const LANGUAGE_OPTIONS = [
  "English", "French", "Spanish", "German", "Italian",
  "Japanese", "Portuguese", "Mandarin", "Korean",
];

const sectionHeaderStyle = {
  fontSize: 10,
  fontFamily: FONT.mono,
  color: COLOR.inkMid,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  borderBottom: `1px solid ${COLOR.borderWarm}`,
  paddingBottom: 4,
  marginBottom: 16,
  marginTop: 32,
};

const labelStyle = { fontSize: 13, color: COLOR.inkDark, fontWeight: 500 };
const descStyle = { fontSize: 11, color: COLOR.inkMid, marginTop: 2, marginBottom: 8 };

const comingSoonBadge = (
  <span style={{
    fontSize: 9,
    fontFamily: FONT.mono,
    background: COLOR.blush,
    color: COLOR.mauve,
    padding: "2px 6px",
    borderRadius: 3,
    letterSpacing: "0.08em",
    marginLeft: 8,
  }}>
    coming soon
  </span>
);

function SegmentedControl({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 0, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 6, overflow: "hidden" }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: "6px 16px",
            fontSize: 12,
            fontFamily: FONT.mono,
            border: "none",
            cursor: "pointer",
            transition: "background 0.15s",
            background: value === opt.value ? COLOR.mauve : COLOR.white,
            color: value === opt.value ? COLOR.white : COLOR.inkDark,
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();

  const [photoAIFilter, setPhotoAIFilter] = useState(() => getSetting(K.photoAIFilter));
  const [photoAIModel, setPhotoAIModel] = useState(() => getSetting(K.photoAIModel));
  const [aiModel, setAiModel] = useState(() => getSetting(K.aiModel));
  const [extendedCaching, setExtendedCaching] = useState(() => getSetting(K.extendedCaching));
  const [currency, setCurrency] = useState(() => getSetting(K.currency) || 'USD');
  const [language, setLanguage] = useState(() => getSetting(K.language) || 'English');
  const [showAvantLeDepart, setShowAvantLeDepart] = useState(() => getSetting(K.showAvantLeDepart) ?? true);
  const [avantScope, setAvantScope] = useState(() => getSetting(K.avantScope) || 'all');
  const [showCostEstimates, setShowCostEstimates] = useState(() => getSetting(K.showCostEstimates) ?? true);
  const [showBookingInfo, setShowBookingInfo] = useState(() => getSetting(K.showBookingInfo) ?? true);
  const [showLocation, setShowLocation] = useState(() => getSetting(K.showLocation) ?? true);

  const updateSetting = (key, value, setter) => {
    setSetting(key, value);
    setter(value);
  };

  return (
    <div style={{ minHeight: "100vh", background: COLOR.cream, fontFamily: FONT.serif }}>
      <div style={{ maxWidth: 480, margin: "0 auto", padding: 24 }}>

        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "10px 16px 10px 0", minHeight: 44, display: "flex", alignItems: "center",
            fontFamily: FONT.serif, fontSize: 15, color: COLOR.inkMid, marginBottom: 8,
          }}
        >
          <Icon name="back" size={18} color={COLOR.inkMid} /> Back
        </button>

        {/* Title */}
        <div style={{ fontSize: 28, fontFamily: FONT.display, color: COLOR.inkDark, fontWeight: 400 }}>
          Settings
        </div>
        <div style={{ fontSize: 11, fontFamily: FONT.mono, color: COLOR.inkMid, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 4 }}>
          Wanderologie preferences
        </div>

        {/* ── SECTION 1: PHOTO SETTINGS ──────────────────────────── */}
        <div style={sectionHeaderStyle}>Photo Settings</div>

        {/* A: AI filter toggle */}
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Improve photo selection with AI</div>
          <div style={descStyle}>Uses AI to filter Pexels results for the best match. Slightly slower.</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={photoAIFilter}
            onChange={(v) => updateSetting(K.photoAIFilter, v, setPhotoAIFilter)}
          />
        </div>

        {/* B: Photo AI model — only when filter is on */}
        {photoAIFilter && (
          <div style={{ marginBottom: 20 }}>
            <div style={labelStyle}>Photo AI model</div>
            <div style={descStyle}>Model used to evaluate photos. More models coming soon.</div>
            <SegmentedControl
              options={PHOTO_MODEL_OPTIONS}
              value={photoAIModel}
              onChange={(v) => updateSetting(K.photoAIModel, v, setPhotoAIModel)}
            />
          </div>
        )}

        {/* C: Photo source — display only */}
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Photo source</div>
          <div style={{ ...descStyle, fontStyle: "italic" }}>Pexels · more coming soon</div>
        </div>

        {/* ── SECTION 2: AI SETTINGS ─────────────────────────────── */}
        <div style={sectionHeaderStyle}>AI Settings</div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>AI model</div>
          <div style={descStyle}>Model used for trip and activity generation.</div>
          <SegmentedControl
            options={AI_MODEL_OPTIONS}
            value={aiModel}
            onChange={(v) => updateSetting(K.aiModel, v, setAiModel)}
          />
        </div>

        {/* ── SECTION 3: DISPLAY ─────────────────────────────────── */}
        <div style={sectionHeaderStyle}>Display</div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Currency</div>
          <div style={descStyle}>Currency symbol shown on activity costs.</div>
          <select
            value={currency}
            onChange={(e) => updateSetting(K.currency, e.target.value, setCurrency)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
              borderRadius: 6, padding: "8px 12px",
              fontSize: 13, fontFamily: FONT.mono,
              color: COLOR.inkDark, cursor: "pointer",
              outline: "none",
            }}
          >
            {CURRENCY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Language</div>
          <div style={descStyle}>Language for AI-generated trip content. Does not change the app interface.</div>
          <select
            value={language}
            onChange={(e) => updateSetting(K.language, e.target.value, setLanguage)}
            style={{
              width: "100%", boxSizing: "border-box",
              background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
              borderRadius: 6, padding: "8px 12px",
              fontSize: 13, fontFamily: FONT.mono,
              color: COLOR.inkDark, cursor: "pointer",
              outline: "none",
            }}
          >
            {LANGUAGE_OPTIONS.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Color scheme {comingSoonBadge}</div>
        </div>

        {/* ── SECTION 4: ADVANCED ────────────────────────────────── */}
        <div style={sectionHeaderStyle}>Advanced</div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Extended caching {comingSoonBadge}</div>
          <div style={descStyle}>Reduces AI costs for repeated requests. Recommended on.</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={extendedCaching}
            onChange={(v) => updateSetting(K.extendedCaching, v, setExtendedCaching)}
          />
        </div>

        {/* ── SECTION 5: CHECKLISTS & BOOKING ────────────────── */}
        <div style={sectionHeaderStyle}>Checklists & Booking</div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Show Avant le Départ</div>
          <div style={descStyle}>Display the pre-trip booking checklist on each trip</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={showAvantLeDepart}
            onChange={(v) => updateSetting(K.showAvantLeDepart, v, setShowAvantLeDepart)}
          />
        </div>

        {showAvantLeDepart && (
          <div style={{ marginBottom: 20, paddingLeft: 16 }}>
            <div style={labelStyle}>Checklist scope</div>
            <div style={descStyle}>Which activities to include in the checklist.</div>
            <SegmentedControl
              options={[{ label: "All activities", value: "all" }, { label: "Confirmed only", value: "confirmed" }]}
              value={avantScope}
              onChange={(v) => updateSetting(K.avantScope, v, setAvantScope)}
            />
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Show cost estimates</div>
          <div style={descStyle}>Display specific dollar amounts on activities. Cost range badges ($ $$ $$$) always shown.</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={showCostEstimates}
            onChange={(v) => updateSetting(K.showCostEstimates, v, setShowCostEstimates)}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Show booking info</div>
          <div style={descStyle}>Display booking status, links, and notes. Can be overridden per trip.</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={showBookingInfo}
            onChange={(v) => updateSetting(K.showBookingInfo, v, setShowBookingInfo)}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={labelStyle}>Show location</div>
          <div style={descStyle}>Display location details on activities. Can be overridden per trip.</div>
          <SegmentedControl
            options={[{ label: "Yes", value: true }, { label: "No", value: false }]}
            value={showLocation}
            onChange={(v) => updateSetting(K.showLocation, v, setShowLocation)}
          />
        </div>

      </div>
    </div>
  );
}
